// src/App.jsx
import React, { useCallback, useState, useEffect, forwardRef, useImperativeHandle } from 'react';
import ReactFlow, {
  addEdge,
  Background,
  useNodesState,
  useEdgesState,
  SelectionMode
} from 'reactflow';
import { useTheme } from '@mui/material/styles';
import {
  ChatRounded,
  AnnouncementRounded,
  MarkChatReadRounded,
  TryRounded,
  CenterFocusStrong,
  ContentPasteRounded,
  CategoryRounded,
  Schedule,
  Numbers,
  Casino,
  AttachMoney,
  Palette,
  Favorite,
  Wallpaper
} from '@mui/icons-material';
import { Menu, MenuItem, ListItemIcon, ListItemText, Divider } from '@mui/material';
import 'reactflow/dist/style.css';
import './App.css';
import { Tooltip } from '@mui/material';
import { DialogueNode } from './components/DialogueNode';
import { BranchNode } from './components/BranchNode';
import { TriggerNode } from './components/TriggerNode';
import { KinkNode } from './components/KinkNode';
import { RelationshipNode } from './components/RelationshipNode';
import { PremiumNode } from './components/PremiumNode';
import { SkinNode } from './components/SkinNode';
import { ChapterNode } from './components/ChapterNode';
import { StartNode } from './components/StartNode';
import { SwitchNode } from './components/SwitchNode';
import { SessionNumberNode } from './components/SessionNumberNode';
import { RandomNode } from './components/RandomNode';
import { BackgroundNode } from './components/BackgroundNode';
import { Box } from '@mui/material';
import FlowBackupList from '../FlowBackupList';

const nodeTypes = {
  start: StartNode,
  dialogue: DialogueNode,
  branch: BranchNode,
  trigger: TriggerNode,
  kink: KinkNode,
  relationship: RelationshipNode,
  premium: PremiumNode,
  skin: SkinNode,
  chapter: ChapterNode,
  switch: SwitchNode,
  sessionNumber: SessionNumberNode,
  random: RandomNode,
  background: BackgroundNode
};

const panOnDrag = [1, 2];

const Flow = forwardRef(({ flowId: propFlowId, flowName, activeLanguage = 'ru', onSave, onClose, onNotification }, ref) => {
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const [reactFlowInstance, setReactFlowInstance] = useState(null);
  const [, setFlowId] = useState(null);
  const [draggedType, setDraggedType] = useState(null);
  const [dragPreview, setDragPreview] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [contextMenu, setContextMenu] = useState(null);
  const [clipboardText, setClipboardText] = useState(null);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [backupDialogOpen, setBackupDialogOpen] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);

  // Модифицированный onNodesChange для предотвращения удаления стартовой ноды
  const onNodesChangeWithProtection = useCallback((changes) => {
    const safeChanges = changes.filter(change => {
      if (change.type === 'remove') {
        const nodeToRemove = nodes.find(node => node.id === change.id);
        return nodeToRemove?.type !== 'start';
      }
      return true;
    });
    
    onNodesChange(safeChanges);
  }, [nodes, onNodesChange]);

  const onConnect = useCallback((params) => setEdges((eds) => addEdge(params, eds)), [setEdges]);

  const onDrop = useCallback(
    (event) => {
      event.preventDefault();

      const type = event.dataTransfer.getData('application/reactflow');
      if (!type) return;
      
      const position = reactFlowInstance.screenToFlowPosition({
        x: event.clientX - 150 * reactFlowInstance.getViewport().zoom,
        y: event.clientY - 25 * reactFlowInstance.getViewport().zoom
      });

      const newNode = {
        id: `${type}-${new Date().getTime()}`,
        type,
        position,
        data: createNodeData(type, `${type}-${new Date().getTime()}`)
      };

      setNodes((nds) => nds.concat(newNode));
    },
    [reactFlowInstance, setNodes] // eslint-disable-line react-hooks/exhaustive-deps -- createNodeData each render
  );

  const onDragOver = useCallback((event) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
  }, []);

  const createNodeInCenter = useCallback((type) => {
    if (!reactFlowInstance) return;

    const center = reactFlowInstance.getViewport();
    const centerX = -center.x + window.innerWidth / 2 - 320;
    const centerY = -center.y + window.innerHeight / 2 - 50;

    const newNode = {
      id: `${type}-${new Date().getTime()}`,
      type,
      position: { x: centerX, y: centerY },
      data: createNodeData(type, `${type}-${new Date().getTime()}`)
    };

    setNodes((nds) => nds.concat(newNode));
  }, [reactFlowInstance, setNodes]); // eslint-disable-line react-hooks/exhaustive-deps -- createNodeData each render

  const handleSave = useCallback(async (isAutoSave = false) => {
    console.log('Flow handleSave called, propFlowId:', propFlowId);
    if (!reactFlowInstance || isSaving || !isLoaded) return;
    
    setIsSaving(true);
    
    if (onNotification && !isAutoSave) {
      onNotification({
        open: true,
        status: 'PENDING',
        message: 'Saving flow…',
        jobId: propFlowId
      });
    }
    
    const viewport = reactFlowInstance.getViewport();
    
    const flowData = {
      nodes: nodes,
      edges: edges,
      viewport: {
        x: viewport.x,
        y: viewport.y,
        zoom: viewport.zoom
      }
    };
    
    try {
      console.log('Saving flow data:', flowData);
      const API_BASE_URL = process.env.NODE_ENV === 'development' ? 'http://localhost:4000' : '';
      const response = await fetch(`${API_BASE_URL}/api/flows/${propFlowId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(flowData)
      });
      
      console.log('Save response status:', response.status);
      
      if (!response.ok) {
        throw new Error('Save failed');
      }
      
      console.log('Flow saved successfully');
      if (onNotification && !isAutoSave) {
        onNotification({
          open: true,
          status: 'COMPLETED',
          message: 'Flow saved successfully.',
          jobId: propFlowId
        });
      }
      
    } catch (error) {
      console.error('Error saving flow:', error);
      if (onNotification && !isAutoSave) {
        onNotification({
          open: true,
          status: 'FAILED',
          message: 'Failed to save flow',
          jobId: propFlowId
        });
      }
    } finally {
      setIsSaving(false);
    }
  }, [nodes, edges, reactFlowInstance, propFlowId, isSaving, isLoaded, onNotification]);

  // Экспортируем функции через ref
  useImperativeHandle(ref, () => ({
    handleSave
  }), [handleSave]);

  const loadFlow = useCallback(async (id) => {
    try {
      const API_BASE_URL = process.env.NODE_ENV === 'development' ? 'http://localhost:4000' : '';
      const response = await fetch(`${API_BASE_URL}/api/flows/${id}`, { cache: 'no-store' });
      
      if (!response.ok) {
        throw new Error('Failed to load flow');
      }
      
      const flowData = await response.json();

      let flowNodes = flowData.nodes || [];
        
      // Проверяем наличие стартовой ноды
      const startNodeExists = flowNodes.some(node => node.type === 'start');
        
      // Если стартовой ноды нет, добавляем её
      if (!startNodeExists) {
        const center = reactFlowInstance.getViewport();
        const centerX = -center.x + window.innerWidth / 2 - 540;
        const centerY = -center.y + window.innerHeight / 2 - 50;
        flowNodes = [
          {
            id: 'start-node',
            type: 'start',
            position: { x: centerX, y: centerY },
            data: {},
            deletable: false
          },
          ...flowNodes
        ];
      }

      setFlowId(propFlowId || id);

      const nodesWithCallbacks = flowNodes.map(node => {
        const nodeData = createNodeData(node.type, node.id);
        // Preserve server data entirely; inject only handler and activeLanguage
        return {
          ...node,
          data: {
            ...node.data,
            activeLanguage,
            onChange: nodeData.onChange
          }
        };
      });

      setNodes(nodesWithCallbacks || []);
      setEdges(flowData.edges || []);
      setIsLoaded(true);
      
      if (reactFlowInstance && flowData.viewport) {
        const { x, y, zoom } = flowData.viewport;
        
        // reactFlowInstance.setViewport(
        //   { x: 0, y: 0, zoom: 1 },
        //   { duration: 0 }
        // );
        
        setTimeout(() => {
          reactFlowInstance.setViewport(
            { x, y, zoom },
            { duration: 500 }
          );
        }, 50);
      }
      
    } catch (error) {
      console.error('Error loading flow:', error);
    }
  }, [reactFlowInstance]); // eslint-disable-line react-hooks/exhaustive-deps -- avoid reload loops; propFlowId handled in effect

  useEffect(() => {
    if (propFlowId && reactFlowInstance) {
      loadFlow(propFlowId);
    }
  }, [loadFlow, reactFlowInstance, propFlowId]);

  // Автоматическое сохранение каждую минуту (после полной загрузки)
  useEffect(() => {
    if (propFlowId && reactFlowInstance && isLoaded) {
      const interval = setInterval(() => {
        handleSave(true);
      }, 60000);
      return () => clearInterval(interval);
    }
  }, [propFlowId, reactFlowInstance, handleSave, isLoaded]);

  const createNodeData = (type, nodeId) => {
    const baseData = { activeLanguage };
    const id = propFlowId;

    switch (type) {
      case 'dialogue':
        return {
          ...baseData,
          text: '',
          enText: '',
          flowId: id,
          answers: [],
          onChange: (text) => {
            setNodes((nds) =>
              nds.map((node) => {
                if (node.id === nodeId) {
                  return {
                    ...node,
                    data: { ...node.data, text }
                  };
                }
                return node;
              })
            );
          }
        };
      case 'trigger':
      case 'branch':
        return {
          ...baseData,
          triggerId: null,
          triggerName: null,
          onChange: (triggerData) => {
            setNodes((nds) =>
              nds.map((node) => {
                if (node.id === nodeId) {
                  return {
                    ...node,
                    data: {
                      ...node.data,
                      triggerId: triggerData.triggerId,
                      triggerName: triggerData.triggerName
                    }
                  };
                }
                return node;
              })
            );
          }
        };
      case 'background':
        return {
          ...baseData,
          backgroundImageId: null,
          backgroundImageUrl: null,
          backgroundThumbnailUrl: null,
          backgroundAssetKind: 'background',
          effectShake: false,
          effectFadeBlack: false,
          onChange: (bgData) => {
            setNodes((nds) =>
              nds.map((node) => {
                if (node.id === nodeId) {
                  return {
                    ...node,
                    data: {
                      ...node.data,
                      ...bgData
                    }
                  };
                }
                return node;
              })
            );
          }
        };
      case 'kink':
        return {
          ...baseData,
          kinkId: null,
          kinkName: null,
          kinkInstruction: null,
          onChange: (kinkData) => {
            setNodes((nds) =>
              nds.map((node) => {
                if (node.id === nodeId) {
                  return {
                    ...node,
                    data: { 
                      ...node.data, 
                      kinkId: kinkData.kinkId,
                      kinkName: kinkData.kinkName,
                      kinkInstruction: kinkData.kinkInstruction
                    }
                  };
                }
                return node;
              })
            );
          }
        };
      case 'timeskip':
        return {
          ...baseData,
          timeValue: 0,
          onChange: (timeValue) => {
            setNodes((nds) =>
              nds.map((node) => {
                if (node.id === nodeId) {
                  return {
                    ...node,
                    data: { 
                      ...node.data, 
                      timeValue
                    }
                  };
                }
                return node;
              })
            );
          }
        };
      case 'relationship':
        return {
          ...baseData,
          onChange: (value) => {
            setNodes((nds) => nds.map(node => node.id === nodeId ? { ...node, data: { ...node.data, value } } : node));
          }
        };
      case 'premium':
        return {
          ...baseData,
          value: 0,
          onChange: (value) => {
            setNodes((nds) => nds.map(node => node.id === nodeId ? { ...node, data: { ...node.data, value } } : node));
          }
        };
      case 'skin':
        return {
          ...baseData,
          value: 'AnalPlug',
          onChange: (value) => {
            setNodes((nds) => nds.map(node => node.id === nodeId ? { ...node, data: { ...node.data, value } } : node));
          }
        };
      case 'switch':
        return {
          ...baseData,
          items: [],
          onChange: (items) => {
            setNodes((nds) =>
              nds.map((node) => {
                if (node.id === nodeId) {
                  return {
                    ...node,
                    data: { ...node.data, items }
                  };
                }
                return node;
              })
            );
          }
        };
      case 'sessionNumber':
        return {
          ...baseData
        };
      case 'random':
        return {
          ...baseData
        };
      default:
        return baseData;
    }
  };

  // propagate activeLanguage to all nodes when it changes
  useEffect(() => {
    setNodes((nds) => nds.map((node) => ({
      ...node,
      data: { ...node.data, activeLanguage }
    })));
  }, [activeLanguage, setNodes]);

  const onNodesDelete = useCallback((nodesToDelete) => {
    // Проверяем каждую ноду в массиве удаляемых нод
    const startNodeToDelete = nodesToDelete.find(node => node.type === 'start');
    if (startNodeToDelete) {
      return false; // Запрещаем удаление если есть стартовая нода
    }
    return true; // Разрешаем удаление для всех остальных типов нод
  }, []);

  const onTouchStart = (type) => (event) => {
    setDraggedType(type);
    
    // Создаем превью перетаскивания
    const touch = event.touches[0];
    setDragPreview({
      x: touch.clientX,
      y: touch.clientY
    });
  };

  const onTouchMove = (event) => {
    if (!draggedType || !dragPreview) return;
    
    event.preventDefault();
    const touch = event.touches[0];
    setDragPreview({
      x: touch.clientX,
      y: touch.clientY
    });
  };

  const onTouchEnd = (event) => {
    if (!draggedType || !dragPreview || !reactFlowInstance) return;

    const position = reactFlowInstance.screenToFlowPosition({
      x: dragPreview.x - 150 * reactFlowInstance.getViewport().zoom,
      y: dragPreview.y - 25 * reactFlowInstance.getViewport().zoom
    });

    const newNode = {
      id: `${draggedType}-${new Date().getTime()}`,
      type: draggedType,
      position,
      data: createNodeData(draggedType, `${draggedType}-${new Date().getTime()}`)
    };

    setNodes((nds) => nds.concat(newNode));
    
    // Очищаем состояние перетаскивания
    setDraggedType(null);
    setDragPreview(null);
  };

  const handleContextMenu = useCallback(async (event) => {
    event.preventDefault();
    if (!reactFlowInstance) return;

    // Проверяем, что клик был не на ноде
    const targetClasses = event.target.classList;
    if (targetClasses.contains('react-flow__node') || 
        event.target.closest('.react-flow__node')) {
      return;
    }

    // Проверяем буфер обмена на наличие и текста, и изображений
    try {
      let hasClipboardContent = false;

      // Проверяем наличие изображения
      const clipboardItems = await navigator.clipboard.read();
      for (const clipboardItem of clipboardItems) {
        if (clipboardItem.types.includes('image/png') || 
            clipboardItem.types.includes('image/jpeg')) {
          hasClipboardContent = true;
          break;
        }
      }

      // Если изображения нет, проверяем текст
      if (!hasClipboardContent) {
        const text = await navigator.clipboard.readText();
        hasClipboardContent = !!text;
      }

      setClipboardText(hasClipboardContent ? 'has-content' : null);
    } catch (err) {
      setClipboardText(null);
    }

    const position = reactFlowInstance.screenToFlowPosition({
      x: event.clientX,
      y: event.clientY
    });

    setContextMenu({
      mouseX: event.clientX,
      mouseY: event.clientY,
      flowPosition: position
    });
  }, [reactFlowInstance]);

  const handleContextMenuClose = () => {
    setContextMenu(null);
  };

  const handleContextMenuClick = (type) => () => {
    if (!contextMenu) return;

    const newNode = {
      id: `${type}-${new Date().getTime()}`,
      type,
      position: contextMenu.flowPosition,
      data: createNodeData(type, `${type}-${new Date().getTime()}`)
    };

    setNodes((nds) => nds.concat(newNode));
    handleContextMenuClose();
  };

  const handlePasteFromClipboard = async (position) => {
    try {
      // Проверяем наличие изображения в буфере обмена
      const clipboardItems = await navigator.clipboard.read();
      for (const clipboardItem of clipboardItems) {
        if (clipboardItem.types.includes('image/png') || 
            clipboardItem.types.includes('image/jpeg')) {
          
          // Создаем ID для новой ноды
          const nodeId = `image-${new Date().getTime()}`;
          
          // Сначала создаем ноду с состоянием загрузки
          const loadingNode = {
            id: nodeId,
            type: 'image',
            position,
            data: {
              ...createNodeData('image', nodeId),
              loading: true // Добавляем флаг загрузки
            },
            selected: true
          };

          // Добавляем ноду в состояние загрузки
          setNodes((nds) => nds.map(n => ({ ...n, selected: false })).concat(loadingNode));

          // Получаем изображение из буфера обмена
          const imageType = clipboardItem.types.find(type => type.startsWith('image/'));
          const blob = await clipboardItem.getType(imageType);

          // Отправляем изображение на сервер
          const response = await fetch('/api/images', {
            method: 'POST',
            body: blob,
            headers: {
              'Content-Type': imageType
            },
            credentials: 'include'
          });

          if (!response.ok) {
            throw new Error('Failed to upload image');
          }

          const image = await response.json();

          // Обновляем ноду с полученными данными
          setNodes((nds) => nds.map(node => 
            node.id === nodeId ? {
              ...node,
              data: {
                ...node.data,
                loading: false,
                imageId: image._id,
                imageUrl: image.url,
                preview: image.preview,
                imagePreview512: image.preview512,
                imagePreview256: image.preview256,
                imageFilename: image.filename
              }
            } : node
          ));

          return;
        }
      }

      // Если изображения нет, обрабатываем как текст
      const text = await navigator.clipboard.readText();
      if (!text || !reactFlowInstance) return;

      const newNode = {
        id: `dialogue-${new Date().getTime()}`,
        type: 'dialogue',
        position,
        data: {
          ...createNodeData('dialogue', `dialogue-${new Date().getTime()}`),
          text
        },
        selected: true
      };

      setNodes((nds) => nds.map(n => ({ ...n, selected: false })).concat(newNode));
    } catch (err) {
      console.error('Failed to process clipboard content:', err);
    }
  };

  // Обработчик для контекстного меню
  const handlePasteClipboard = () => {
    if (!contextMenu) return;
    handlePasteFromClipboard(contextMenu.flowPosition);
    handleContextMenuClose();
  };

  // Обработчик для Ctrl+V/Command+V
  useEffect(() => {
    const handleKeyDown = async (event) => {
      // Проверяем сочетание клавиш независимо от раскладки
      if ((event.ctrlKey || event.metaKey) && event.code === 'KeyV') {
        // Проверяем, что никакой элемент ввода не в фокусе
        if (document.activeElement.tagName === 'INPUT' || 
            document.activeElement.tagName === 'TEXTAREA' ||
            document.activeElement.isContentEditable) {
          return;
        }

        const position = reactFlowInstance.screenToFlowPosition({
          x: mousePosition.x - 150 * reactFlowInstance.getViewport().zoom,
          y: mousePosition.y - 25 * reactFlowInstance.getViewport().zoom
        });

        handlePasteFromClipboard(position);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [mousePosition, reactFlowInstance]); // eslint-disable-line react-hooks/exhaustive-deps -- paste handler uses live flow closure

  const renderContextMenu = () => (
    <Menu
      open={contextMenu !== null}
      onClose={handleContextMenuClose}
      anchorReference="anchorPosition"
      anchorPosition={
        contextMenu !== null
          ? { top: contextMenu.mouseY, left: contextMenu.mouseX }
          : undefined
      }
    >
      {[
        ...(clipboardText ? [
          { type: 'paste', icon: <ContentPasteRounded />, label: 'Paste Clipboard', onClick: handlePasteClipboard },
          { type: 'divider', component: <Divider /> }
        ] : []),
        { type: 'dialogue', icon: <ChatRounded />, label: 'Dialogue' },
        { type: 'trigger', icon: <AnnouncementRounded />, label: 'Trigger' },
        { type: 'kink', icon: <Favorite />, label: 'Kink' },
        { type: 'branch', icon: <MarkChatReadRounded />, label: 'Branch' },
        { type: 'relationship', icon: <TryRounded />, label: 'Relationship' },
        { type: 'premium', icon: <AttachMoney />, label: 'Premium' },
        { type: 'skin', icon: <Palette />, label: 'Skin' },
        { type: 'background', icon: <Wallpaper />, label: 'Background' },
        { type: 'switch', icon: <CategoryRounded />, label: 'Switch' },
        { type: 'sessionNumber', icon: <Numbers />, label: 'Session Number' },
        { type: 'random', icon: <Casino />, label: 'Random' }
      ].map(({ type, icon, label, onClick, component }) => 
        component ? (
          <div key={type}>{component}</div>
        ) : (
          <MenuItem key={type} onClick={onClick || handleContextMenuClick(type)}>
            <ListItemIcon>{icon}</ListItemIcon>
            <ListItemText>{label}</ListItemText>
          </MenuItem>
        )
      )}
    </Menu>
  );

  // Добавляем слежение за позицией мыши
  useEffect(() => {
    const handleMouseMove = (event) => {
      setMousePosition({ x: event.clientX, y: event.clientY });
    };

    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  return (
    <div 
      className={isDarkMode ? 'dark-nodes' : 'light-nodes'} 
      style={{ display: 'flex', height: '100%', cursor: 'default' }}
      onTouchMove={onTouchMove}
      onTouchEnd={onTouchEnd}
    >
      <Box
        component="aside"
        sx={{
          borderRight: `${isDarkMode ? '0' : '1px solid #e0e0e0'}`,
          width: {
            xs: '100%',  // На мобильных - во всю ширину
            sm: 60       // На десктопе - 60px
          },
          height: {
            xs: 'auto',  // На мобильных - автоматическая высота
            sm: '100%'   // На десктопе - на всю высоту
          },
          padding: {
            xs: '4px 5px',  // Больше горизонтального отступа на мобильных
            sm: 2
          },
          background: isDarkMode ? '#1e1e1e' : '#f5f5f5',
          color: isDarkMode ? 'white' : 'black',
          display: 'flex',
          flexDirection: {
            xs: 'row',   // На мобильных - горизонтальное расположение
            sm: 'column' // На десктопе - вертикальное расположение
          },
          " > div > div": {
            margin: 0,
          },
          flexWrap: {
            xs: 'wrap',  // На мобильных - перенос на новую строку
            sm: 'nowrap' // На десктопе - без переноса
          },
          gap: '4px',
          alignItems: 'center',
          justifyContent: 'flex-start',
          zIndex: 5,
          position: {
            xs: 'fixed',  // На мобильных - фиксированное позиционирование
            sm: 'relative'
          },
          bottom: {
            xs: 0,        // На мобильных - прижать к низу
            sm: 'auto'
          },
          left: {
            xs: 0,        // На мобильных - от левого края
            sm: 'auto'
          }
        }}
      >
        <Tooltip title="Dialogue" placement="right">
          <div
            onDragStart={(event) => event.dataTransfer.setData('application/reactflow', 'dialogue')}
            draggable
            className="node-button"
            onClick={() => createNodeInCenter('dialogue')}
            onTouchStart={onTouchStart('dialogue')}
          >
            <ChatRounded sx={{ fontSize: 28 }} />
          </div>
        </Tooltip>
        <Tooltip title="Trigger" placement="right">
          <div
            onDragStart={(event) => event.dataTransfer.setData('application/reactflow', 'trigger')}
            draggable
            className="node-button"
            onClick={() => createNodeInCenter('trigger')}
            onTouchStart={onTouchStart('trigger')}
          >
            <AnnouncementRounded sx={{ fontSize: 28 }} />
          </div>
        </Tooltip>
        <Tooltip title="Kink" placement="right">
          <div
            onDragStart={(event) => event.dataTransfer.setData('application/reactflow', 'kink')}
            draggable
            className="node-button"
            onClick={() => createNodeInCenter('kink')}
            onTouchStart={onTouchStart('kink')}
          >
            <Favorite sx={{ fontSize: 28 }} />
          </div>
        </Tooltip>
        <Tooltip title="Branch" placement="right">
          <div
            onDragStart={(event) => event.dataTransfer.setData('application/reactflow', 'branch')}
            draggable
            className="node-button"
            onClick={() => createNodeInCenter('branch')}
            onTouchStart={onTouchStart('branch')}
          >
            <MarkChatReadRounded sx={{ fontSize: 28 }} />
          </div>
        </Tooltip>
        <Tooltip title="Relationship" placement="right">
          <div
            onDragStart={(event) => event.dataTransfer.setData('application/reactflow', 'relationship')}
            draggable
            className="node-button"
            onClick={() => createNodeInCenter('relationship')}
            onTouchStart={onTouchStart('relationship')}
          >
            <TryRounded sx={{ fontSize: 28 }} />
          </div>
        </Tooltip>
        <Tooltip title="Premium" placement="right">
          <div
            onDragStart={(event) => event.dataTransfer.setData('application/reactflow', 'premium')}
            draggable
            className="node-button"
            onClick={() => createNodeInCenter('premium')}
            onTouchStart={onTouchStart('premium')}
          >
            <AttachMoney sx={{ fontSize: 28 }} />
          </div>
        </Tooltip>
        <Tooltip title="Skin" placement="right">
          <div
            onDragStart={(event) => event.dataTransfer.setData('application/reactflow', 'skin')}
            draggable
            className="node-button"
            onClick={() => createNodeInCenter('skin')}
            onTouchStart={onTouchStart('skin')}
          >
            <Palette sx={{ fontSize: 28 }} />
          </div>
        </Tooltip>
        <Tooltip title="Background" placement="right">
          <div
            onDragStart={(event) => event.dataTransfer.setData('application/reactflow', 'background')}
            draggable
            className="node-button"
            onClick={() => createNodeInCenter('background')}
            onTouchStart={onTouchStart('background')}
          >
            <Wallpaper sx={{ fontSize: 28 }} />
          </div>
        </Tooltip>
        <Tooltip title="Switch" placement="right">
          <div
            onDragStart={(event) => event.dataTransfer.setData('application/reactflow', 'switch')}
            draggable
            className="node-button"
            onClick={() => createNodeInCenter('switch')}
            onTouchStart={onTouchStart('switch')}
          >
            <CategoryRounded sx={{ fontSize: 28 }} />
          </div>
        </Tooltip>
        <Tooltip title="Session Number" placement="right">
          <div
            onDragStart={(event) => event.dataTransfer.setData('application/reactflow', 'sessionNumber')}
            draggable
            className="node-button"
            onClick={() => createNodeInCenter('sessionNumber')}
            onTouchStart={onTouchStart('sessionNumber')}
          >
            <Numbers sx={{ fontSize: 28 }} />
          </div>
        </Tooltip>
        <Tooltip title="Random" placement="right">
          <div
            onDragStart={(event) => event.dataTransfer.setData('application/reactflow', 'random')}
            draggable
            className="node-button"
            onClick={() => createNodeInCenter('random')}
            onTouchStart={onTouchStart('random')}
          >
            <Casino sx={{ fontSize: 28 }} />
          </div>
        </Tooltip>
        <div style={{ flex: 1 }} />
        <Tooltip title="Center View" placement="right">
          <div className="node-button" style={{ cursor: 'pointer' }} onClick={() => reactFlowInstance.fitView({
            padding: 0.2,
            includeHidden: true,
            minZoom: 0.01,
            maxZoom: 2
          })}
          >
            <CenterFocusStrong sx={{ fontSize: 28 }} />
          </div>
        </Tooltip>
        <Tooltip title="Backups" placement="right">
          <div className="node-button" style={{ cursor: 'pointer' }} onClick={() => setBackupDialogOpen(true)}>
            <Schedule sx={{ fontSize: 28 }} />
          </div>
        </Tooltip>
      </Box>

      <ReactFlow
        nodes={nodes}
        edges={edges}
        fitView={true}
        fitViewOptions={{
          padding: 0.2,
          includeHidden: true,
          minZoom: 0.01,
          maxZoom: 2
        }}
        onNodesChange={onNodesChangeWithProtection}
        onEdgesChange={onEdgesChange}
        onConnect={onConnect}
        nodeTypes={nodeTypes}
        onInit={setReactFlowInstance}
        onDrop={onDrop}
        onDragOver={onDragOver}
        panOnScroll={true}
        panOnDrag={panOnDrag}
        selectionOnDrag={true}
        selectionMode={SelectionMode.Partial}
        proOptions={{ 
          theme: isDarkMode ? 'dark' : 'light',
          hideAttribution: true 
        }}
        style={{ 
          flexGrow: 1, 
          backgroundColor: isDarkMode ? '#121212' : '#ffffff',
        }}
        className="react-flow-canvas"
        zoomOnDoubleClick={false}
        minZoom={0.2}
        maxZoom={2}
        //includeHiddenNodes={true}
        defaultViewport={{ x: 0, y: 0, zoom: 1 }}
        onNodesDelete={onNodesDelete}
        onContextMenu={handleContextMenu}
        deleteKeyCode={['Backspace', 'Delete']}
      >
        <Background 
          color={isDarkMode ? '#333' : '#aaa'} 
          gap={16} 
        />
      </ReactFlow>
      
      {renderContextMenu()}

      <FlowBackupList
        open={backupDialogOpen}
        onClose={() => setBackupDialogOpen(false)}
        flowId={propFlowId}
        flowName={flowName}
        onRestore={() => {
          if (propFlowId && reactFlowInstance) {
            loadFlow(propFlowId);
          }
        }}
      />

    </div>
  );
});

export default Flow;
